import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { useTranslation } from "react-i18next";

import { extractUserListContext, userQueryKeys } from "./query-keys";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";
import { EditUserRequest } from "@/types/request/EditUserRequest";
import { EditUserResponse } from "@/types/response/EditUserResponse";
import { ValidationResponse } from "@/types/response/ValidationResponse";

type UseUpdateUserMutationParams = {
  userId: string;
  tenantId?: string | null;
  clientId?: string | null;
};

/**
 * Hook to update an existing user.
 *
 * @param params - Parameters for the mutation
 * @param params.userId - The ID of the user to update
 * @param params.tenantId - Tenant ID to associate the user with
 * @param params.clientId - Client ID to associate the user with
 *
 * @returns Mutation object for updating a user
 *
 * @example
 * ```tsx
 * const updateUser = useUpdateUserMutation({ userId: "123", tenantId: "456" });
 * updateUser.mutate({ email: "new@example.com", firstName: "Jane", ... });
 * ```
 */
export function useUpdateUserMutation(params: UseUpdateUserMutationParams) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const { t } = useTranslation();

  return useMutation<
    AxiosResponse<EditUserResponse>,
    AxiosError<ValidationResponse>,
    EditUserRequest
  >({
    mutationFn: (editUserRequest: EditUserRequest) =>
      instance.put(`/api/v2/Users/${params.userId}`, {
        ...editUserRequest,
        tenantId: params.tenantId,
        clientId: params.clientId,
      }),
    onSuccess: () => {
      showToast(t("success.updated", { entity: "User" }), "success");

      queryClient.invalidateQueries({
        queryKey: userQueryKeys.detail(params.userId),
      });

      // Refetch user list so overview updates immediately (bypasses staleTime)
      if (params.clientId) {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
          predicate: (query) => {
            const context = extractUserListContext(query.queryKey);
            return context.clientId === params.clientId;
          },
        });
      } else if (params.tenantId) {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
          predicate: (query) => {
            const context = extractUserListContext(query.queryKey);
            return (
              context.tenantId === params.tenantId &&
              (context.clientId === null || context.clientId === undefined)
            );
          },
        });
      } else {
        queryClient.refetchQueries({
          queryKey: userQueryKeys.lists(),
        });
      }
    },
  });
}
